import { useState, useEffect, useRef } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faPhone,
  faNoteSticky,
  faLocation,
  faFlag,
  faBriefcase,
  faPlus,
  faEllipsisV,
  faTrash,
  faCalendar,
  faUser,
  faEnvelope,
} from "@fortawesome/free-solid-svg-icons"
import { Controller, useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { DeleteRequest, GetRequest, PostRequest } from "../../../Utils/hooks/ApiHandler"
import useLocalStorageState from "use-local-storage-state"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"
import { add5HoursTo12HrTime, formatDate } from "../../../Utils/helpers/commonhelper"
import DeleteModal from "../../DeleteModal"
import nodata from "../../../Utils/assets/img/leads_no_data_found.png"
import Select from "react-select"
// Random icons array for followups
const randomIcons = [faPhone, faNoteSticky, faLocation, faFlag, faBriefcase, faCalendar, faUser, faEnvelope]

// Random colors for icons
const randomColors = [
  { backgroundColors: "#cfecff", colors: "#0099ff" },
  { backgroundColors: "#ffeccd", colors: "#eda32a" },
  { backgroundColors: "#dcdcff", colors: "#4f4eab" },
  { backgroundColors: "#ffddd6", colors: "tomato" },
  { backgroundColors: "#c7ffee", colors: "#0dcf94" },
  { backgroundColors: "#ffe6f0", colors: "#e91e63" },
  { backgroundColors: "#f0e6ff", colors: "#9c27b0" },
  { backgroundColors: "#e6fff0", colors: "#4caf50" },
]

// Zod Schema for Validation
const followUpSchema = z.object({
  nextFollowUpDate: z.string().min(1, "Next Follow up date is required"),
  time: z.string().min(1, "Time is required"),
  feedback: z.string().min(1, "Feedback is required"),
  priority: z.string().min(1, "Priority is required"),
})

export const Followups = ({ leadId, followupGrant }) => {
  // State management
  const [expandedItems, setExpandedItems] = useState({})
  const [showAddForm, setShowAddForm] = useState(false)
  const [showTaskActions, setShowTaskActions] = useState({})
  const [followupData, setFollowupData] = useState([])
  const [loading, setLoading] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [followupToDelete, setFollowupToDelete] = useState(null)

  // Auth and user data
  const [customer] = useLocalStorageState("customer", { token: {} })
  const loginUserId = customer?.customer_details?.id || ""
  const token = customer?.token?.authorization || ""
  const rf = customer?.token?.authentication || ""
  const headers = { Authorization: token, Authentication: rf }
  const navigate = useNavigate()
  const extras = { customer, navigate }

  const [isSaving, setIsSaving] = useState(false)

  const wordLimit = 20

  const dropdownRef = useRef(null)
  const deleteRef = useRef(null)
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside of dropdown and not on the trigger button
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        !deleteRef.current?.contains(event.target)
      ) {
        setShowTaskActions({});
      }
    };

    // Add event listener when any dropdown is open
    if (Object.keys(showTaskActions).length > 0) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    // Cleanup
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTaskActions]);

  // Get tomorrow's date (yyyy-mm-dd) and current time (HH:mm) for default form values
  const getDefaultDateTime = () => {
    const now = new Date()
    const tomorrow = new Date(now)
    tomorrow.setDate(now.getDate() + 1)

    const yyyy = tomorrow.getFullYear()
    const mm = String(tomorrow.getMonth() + 1).padStart(2, "0")
    const dd = String(tomorrow.getDate()).padStart(2, "0")
    const dateStr = `${yyyy}-${mm}-${dd}`

    const hh = String(now.getHours()).padStart(2, "0")
    const min = String(now.getMinutes()).padStart(2, "0")
    const timeStr = `${hh}:${min}`

    return { dateStr, timeStr }
  }

  const { dateStr, timeStr } = getDefaultDateTime()
  // Form handling
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(followUpSchema),
    defaultValues: {
      nextFollowUpDate: dateStr,
      time: timeStr,
      feedback: "",
      priority: "low",
    },
  })

  // Formats time from 24-hour to 12-hour format
  const formatTime = (timeStr) => {
    // if (!timeStr) return ""
    // let [hour, minute] = timeStr.split(":")
    // hour = Number.parseInt(hour)
    // const ampm = hour >= 12 ? "PM" : "AM"
    // hour = hour % 12 || 12
    // return `${hour}:${minute} ${ampm}`
    if (!timeStr) return ""
    const [hour, minute] = timeStr.split(":").map(Number)
    const date = new Date(Date.UTC(1970, 0, 1, hour, minute))
    return new Intl.DateTimeFormat(undefined, {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }).format(date)
  }

  // Get random icon and color for followup
  const getRandomIconAndColor = (index) => {
    const iconIndex = index % randomIcons.length
    const colorIndex = index % randomColors.length
    return {
      icon: randomIcons[iconIndex],
      ...randomColors[colorIndex]
    }
  }


  // Fetches followup data for the current lead
  const fetchFollowups = async () => {
    if (!leadId || leadId === null) return
    setLoading(true)
    try {
      const res = await GetRequest(`lead/fetchLeadFollowup/${leadId}`, { headers }, extras)

      if (res.status === "fail" || res.success === false) {
        return
      }
      // Transform API data to match component structure
      const transformedData = res.data['feedback'].map((item, index) => {
        const { icon, backgroundColors, colors } = getRandomIconAndColor(index)
        const [date, time] = item.followed_on?.split(" ") || ["", ""]
        const [followUpDate, followUpTime] = item.next_followup?.split(" ") || ["", ""]
        return {
          id: item.feedback_id,
          date: formatDate(date) || "N/A",
          datetime: date,
          time: formatTime(time) || "N/A",
          nextFollowupDate: formatDate(followUpDate) || "N/A",
          text: item.feedback,
          icon,
          backgroundColors,
          colors,
          addedBy: item.followed_by,
          priority: item.priority?.charAt(0).toUpperCase() + item.priority?.slice(1) || "Low",
          feedback_id: item.feedback_id
        }
      })
      setFollowupData(transformedData)
    } catch (err) {
      toast.error("Failed to fetch followup data")
      setFollowupData([])
    } finally {
      setLoading(false)
    }
  }

  // Handles form submission for new followup
  const onSubmit = async (formData) => {
    try {
      const payload = {
        user_login_id: loginUserId,
        lead_id: leadId,
        followup_time: formData.time,
        followup_date: formData.nextFollowUpDate,
        feedback: formData.feedback,
        priority: formData.priority,
      }

      setIsSaving(true)
      const res = await PostRequest(`/lead/takeLeadFollowup`, payload, null, { headers }, extras)
      if (res.status === "success") {
        toast.success(res.message)
        fetchFollowups()
        setIsSaving(false)
        setShowAddForm(false)
        reset()
      } else {
        toast.error(res.message)
      }
    } catch (err) {
      toast.error("Error adding followup")
    }
  }

  const toggleExpand = (index) => {
    setExpandedItems((prev) => ({
      ...prev,
      [index]: !prev[index],
    }))
  }

  const toggleTaskActions = (followupId) => {
    setShowTaskActions((prev) => ({
      ...prev,
      [followupId]: !prev[followupId],
    }))
  }

  // Show delete confirmation modal
  const handleDeleteFollowup = (followup_id) => {
    setFollowupToDelete(followup_id)
    setShowDeleteModal(true)
    setShowTaskActions({})
  }

  // Handle confirmed deletion
  const handleDeleteConfirm = async () => {
    setLoading(true)
    try {
      const res = await DeleteRequest(
        `lead/deleteLeadsFollowup/${followupToDelete}/${loginUserId}`,
        { headers },
        extras
      )

      if (res.status === "success") {
        toast.success("Followup deleted successfully")
        setLoading(false)
        fetchFollowups()
      } else {
        toast.error(res.message || "Failed to delete followup")
        setLoading(false)
      }
    } catch (err) {
      toast.error("Error deleting followup")
      setLoading(false)
    } finally {
      setLoading(false)
      setShowDeleteModal(false)
      setFollowupToDelete(null)
    }
  }

  // Fetch followup data when component mounts or leadId changes
  useEffect(() => {
    if (leadId) {
      fetchFollowups()
    }
  }, [leadId])
  return (
    <div style={{ minHeight: "calc(100vh - 10.5rem)", maxHeight: "calc(100vh - 10.5rem)", overflowY: "auto", overflowX: "hidden", position: 'relative' }}>
      <DeleteModal
        show={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false)
          setFollowupToDelete(null)
        }}
        onConfirm={handleDeleteConfirm}
        isLoading={loading}
        message="Are you sure you want to delete this followup?"
      />

      {/* Header Section */}
      <div className="d-flex justify-content-between align-items-center mb-3" >
        <div className=" mb-0" style={{ fontSize: "19px", fontWeight: 300 }}>
          Followups
        </div>
        <div className="d-flex align-items-center gap-1" style={{ maxHeight: 33 }}>
          <button
            className="btn btn-default-outline"
            style={styles.buttons}
            onClick={() => {
              if (!followupGrant?.isCreate) {
                toast.warning("You are not authorized for this action!")
                return
              }
              const { dateStr, timeStr } = getDefaultDateTime()
              reset({
                nextFollowUpDate: dateStr,
                time: timeStr,
                feedback: "",
                priority: "low",
              })
              setShowAddForm(!showAddForm)
            }}
          >
            <FontAwesomeIcon icon={faPlus} /> Add
          </button>
        </div>
      </div>

      <hr />

      {/* Add Followup Form */}
      {showAddForm && (
        <div className="mb-4 p-3 border rounded" style={{ background: '#fefefe' }}>
          <h6 className="mb-3">Add New Followup</h6>
          <form
            onSubmit={handleSubmit(onSubmit)}
            onKeyDown={(e) => {
              console.log("key", e.key)
              if (e.key === "Enter") {
                e.preventDefault()
                handleSubmit(onSubmit)()
              }
            }}
          >
            <div className="row mb-3">
              <div className="col-md-4">
                <label className="form-label" style={{ textWrap: "nowrap" }}>Next Followup Date <span className="text-danger">*</span></label>
                <input
                  type="date"
                  className={`form-control ${errors.nextFollowUpDate ? 'is-invalid' : ''}`}
                  {...register("nextFollowUpDate")}
                />
                {errors.nextFollowUpDate && (
                  <div className="invalid-feedback ">{errors.nextFollowUpDate.message}</div>
                )}
              </div>
              <div className="col-md-4">
                <label className="form-label">Time <span className="text-danger">*</span></label>
                <input
                  type="time"
                  className={`form-control ${errors.time ? 'is-invalid' : ''}`}
                  {...register("time")}
                />
                {errors.time && (
                  <div className="invalid-feedback">{errors.time.message}</div>
                )}
              </div>
              <div className="col-md-4">
                <label className="form-label">Priority</label>

                <Controller
                  name="priority"
                  control={control}
                  rules={{ required: "Priority is required" }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      options={[
                        { value: "low", label: "Low" },
                        { value: "medium", label: "Medium" },
                        { value: "high", label: "High" }
                      ]}
                      styles={{
                        control: (base) => ({
                          ...base,
                          borderColor: "#e1d4da",
                        }),
                      }}
                      className={errors.priority ? "is-invalid" : ""}
                      onChange={(selected) => field.onChange(selected.value)}
                      value={
                        [
                          { value: "low", label: "Low" },
                          { value: "medium", label: "Medium" },
                          { value: "high", label: "High" }
                        ].find((opt) => opt.value === field.value)
                      }
                    />
                  )}
                />

                {errors.priority && (
                  <div className="invalid-feedback d-block">
                    {errors.priority.message}
                  </div>
                )}
              </div>
              <div className="col-md-12 mt-3">
                <label className="form-label">Feedback <span className="text-danger">*</span></label>
                <textarea
                  className={`form-control ${errors.feedback ? 'is-invalid' : ''}`}
                  rows={2}
                  placeholder="Enter feedback..."
                  {...register("feedback")}
                />
                {errors.feedback && (
                  <div className="invalid-feedback">{errors.feedback.message}</div>
                )}
              </div>
            </div>
            <div className="d-flex gap-2 justify-content-end">
              <button
                type="button"
                className="btn btn-default-outline btnss"
                onClick={() => {
                  setShowAddForm(false)
                  reset()
                }}
                style={styles.listWhatsButtonsStyles}
              >
                Cancel
              </button>
              <button type="submit" className="btn btn-default btnss" disabled={isSaving} style={styles.listWhatsButtonsStyles}>
                {isSaving ? "Saving" : "Save"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Followups List */}
      <div className="mt-0" style={{ paddingTop: '15px' }}>
        {
          followupGrant?.isView ?
            loading ? (
              <div className="text-center py-4">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : followupData.length === 0 ? (
              <div
                class="d-flex justify-content-center align-items-center"
                style={{ position: "absolute", left: "24%" }}
              >
                <img
                  src={nodata}
                  alt=""
                  style={{ width: "17rem", marginTop: "0" }}
                />
              </div>
            ) : (
              <div className="activity-feed-container">
                <ol className="activity-feed listStyleForContact " style={{ overflowX: "scroll", minHeight: "35rem", maxHeight: "35rem", scrollBehavior: "smooth", scrollbarWidth: "thin" }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.scrollbarColor = "#e0e0e0 white"
                  }}>
                  {followupData.map((item, index) => {
                    const isExpanded = expandedItems[index]
                    const text = item?.text || ""
                    const words = typeof text === "string" ? text.split(" ") : []
                    const shouldShowButton = words.length > wordLimit
                    const displayedText = isExpanded
                      ? item.text
                      : words.slice(0, wordLimit).join(" ") + (shouldShowButton ? "..." : "")

                    return (
                      <li key={item?.id} className="feed-item align-items-center position-relative listStyleForContact mb-0 pb-4"
                      >
                        <div
                          className="activity-icon backgroundColors"
                          style={{ background: item?.backgroundColors, color: item?.colors }}
                        >
                          <FontAwesomeIcon icon={item?.icon} />
                        </div>

                        <div className="feed-content ms-0 pe-3">
                          <div className="d-flex justify-content-between align-items-start">
                            <div className="flex-grow-1">
                              <p style={{ color: "#a0a0a0", fontWeight: 300, textTransform: 'capitalize' }} className="mb-0 fs-11">
                                By - {item?.addedBy} on {item?.date} at {(item?.time)}
                              </p>

                              <p className={`text mb-2 ${isExpanded ? "expanded" : "clamped"}`}>
                                {displayedText ? displayedText : item?.text}
                              </p>

                              {shouldShowButton && (
                                <span
                                  onClick={() => toggleExpand(index)}
                                  style={{ color: "blue", cursor: "pointer", marginLeft: "0px", fontSize: 11 }}
                                >
                                  {isExpanded ? "Read less" : "Read more"}
                                </span>
                              )}

                              <div className="d-flex align-items-center justify-content-between gap-3 mt-2">

                                <div className="fs-12">
                                  Priority:
                                  <span
                                    className={`badge ms-1 ${item?.priority === "High" ? "bg-danger" : item?.priority === "Medium" ? "bg-warning" : "bg-success"}`}
                                    style={{ fontSize: "12px", borderRadius: 10 }}
                                  >
                                    {item?.priority}
                                  </span>
                                </div>
                                <p style={{ color: "#a0a0a0" }} className="mb-0 fs-12">
                                  Next Followup Date : {item?.nextFollowupDate}
                                </p>
                              </div>


                            </div>

                            {/* Followup Actions Dropdown */}
                            <div className="position-relative">
                              <button
                                className="btn btn-sm btn-none"
                                onClick={() => toggleTaskActions(item?.id)}
                                style={{ fontSize: "10px", padding: "2px 6px" }}
                              >
                                <FontAwesomeIcon icon={faEllipsisV} />
                              </button>

                              {showTaskActions[item?.id] && (
                                <div
                                  ref={dropdownRef}
                                  className="dropdown-menu show position-absolute"
                                  style={{
                                    right: 0,
                                    top: "100%",
                                    minWidth: "120px",
                                    zIndex: 1000,
                                  }}
                                >
                                  <button
                                    ref={deleteRef}
                                    className="dropdown-item fs-12 d-flex gap-2 align-items-center"
                                    onClick={() => {
                                      if (!followupGrant?.isDelete) {
                                        toast.warning("You are not authorized for this action!")
                                        return
                                      }
                                      handleDeleteFollowup(item?.feedback_id)
                                    }}
                                  >
                                    <FontAwesomeIcon icon={faTrash} /> Delete
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </li>
                    )
                  })}
                </ol>
              </div>
            )
            :
            <>You are not authorized to view follow-ups!</>
        }
      </div>
    </div>
  )
}

const styles = {
  filterbuttons: {
    fontSize: "13px",
    height: "33px",
    width: "33px",
    maxHeight: 33,
    borderRadius: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  buttons: {
    color: "#fff",
    fontSize: "13px",
    height: "34px",
    borderRadius: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
}
